import { useState, useEffect, useRef } from 'react';
import { Room, RoomEvent, Track, ConnectionState } from 'livekit-client';
import { useSessionStore } from '../stores/session';
import { useBusinessStore } from '../stores/business';
import VoiceVisualizer from '../components/VoiceVisualizer';
import TranscriptView from '../components/TranscriptView';

// LiveKit server URL - fetched from backend or use environment variable
const LIVEKIT_SERVER_URL = import.meta.env.VITE_LIVEKIT_SERVER_URL || 'wss://livekit.optimat.io';

// You can also set this in your .env file:
// VITE_LIVEKIT_SERVER_URL=wss://your-livekit-server.com

export default function TestCallPage() {
  const {
    conversationId,
    roomName,
    token,
    status,
    transcript,
    contact,
    isNewContact,
    error,
    isLoading,
    startSession,
    endSession,
    addTranscriptEntry,
    updateStatus,
    reset,
  } = useSessionStore();

  const { businesses, agents, fetchBusinesses, fetchAgents } = useBusinessStore();

  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [roomState, setRoomState] = useState<ConnectionState>(ConnectionState.Disconnected);
  const [agentConnected, setAgentConnected] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);

  // LiveKit refs
  const roomRef = useRef<Room | null>(null);
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());

  // Load businesses on mount
  useEffect(() => {
    if (businesses.length === 0) {
      fetchBusinesses();
    }
  }, [businesses.length, fetchBusinesses]);

  // Load agents when business changes
  useEffect(() => {
    if (selectedBusinessId) {
      fetchAgents(selectedBusinessId);
    }
  }, [selectedBusinessId, fetchAgents]);

  // Set default selections
  useEffect(() => {
    if (businesses.length > 0 && !selectedBusinessId) {
      setSelectedBusinessId(businesses[0].id);
    }
  }, [businesses, selectedBusinessId]);

  useEffect(() => {
    if (agents.length > 0 && !selectedAgentId) {
      const defaultAgent = agents.find((a) => a.isDefault) || agents[0];
      setSelectedAgentId(defaultAgent?.id || null);
    }
  }, [agents, selectedAgentId]);

  // Get selected agent config
  const selectedAgent = agents.find((a) => a.id === selectedAgentId);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      disconnectFromRoom();
    };
  }, []);

  // Connect to LiveKit when we have a token
  useEffect(() => {
    if (token && roomName && status === 'connecting') {
      connectToLiveKit();
    }
  }, [token, roomName, status]);

  const connectToLiveKit = async () => {
    if (!token || !roomName) return;

    setConnectionError(null);
    console.log('Connecting to LiveKit room:', roomName);

    try {
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      roomRef.current = room;

      // Set up event handlers
      room.on(RoomEvent.ConnectionStateChanged, (state) => {
        console.log('Room connection state:', state);
        setRoomState(state);
        
        if (state === ConnectionState.Connected) {
          updateStatus('active');
        } else if (state === ConnectionState.Disconnected) {
          updateStatus('completed');
        }
      });

      room.on(RoomEvent.ParticipantConnected, (participant) => {
        console.log('Participant connected:', participant.identity, participant.metadata);
        // Check if this is the agent (could be named various things)
        const isAgent = participant.identity.toLowerCase().includes('agent') || 
                        participant.identity.toLowerCase().includes('bot') ||
                        participant.identity.toLowerCase().includes('assistant') ||
                        participant.identity.startsWith('agent-');
        
        if (isAgent) {
          setAgentConnected(true);
          addTranscriptEntry({
            id: `sys_${Date.now()}`,
            role: 'system',
            content: `AI Agent "${participant.identity}" connected to the call`,
            timestamp: Date.now(),
            isFinal: true,
          });
        } else {
          console.log('Non-agent participant joined:', participant.identity);
        }
      });

      room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        console.log('Participant disconnected:', participant.identity);
        const isAgent = participant.identity.toLowerCase().includes('agent') || 
                        participant.identity.toLowerCase().includes('bot') ||
                        participant.identity.toLowerCase().includes('assistant') ||
                        participant.identity.startsWith('agent-');
        if (isAgent) {
          setAgentConnected(false);
          addTranscriptEntry({
            id: `sys_${Date.now()}`,
            role: 'system',
            content: 'AI Agent disconnected from the call',
            timestamp: Date.now(),
            isFinal: true,
          });
        }
      });

      room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        console.log('Track subscribed:', track.kind, 'from', participant.identity);
        console.log('Track details:', {
          sid: track.sid,
          source: publication.source,
          muted: track.isMuted,
        });
        
        if (track.kind === Track.Kind.Audio) {
          // Create audio element for playback
          const audioElement = document.createElement('audio');
          audioElement.autoplay = true;
          (audioElement as HTMLAudioElement & { playsInline: boolean }).playsInline = true;
          audioElement.id = `audio-${participant.identity}`;
          audioElement.volume = 1.0;
          
          // Append to document body (required for some browsers)
          document.body.appendChild(audioElement);
          
          // Attach the track to the audio element
          track.attach(audioElement);
          audioElementsRef.current.set(participant.identity, audioElement);
          
          console.log('Audio element created and attached:', {
            id: audioElement.id,
            autoplay: audioElement.autoplay,
            volume: audioElement.volume,
            paused: audioElement.paused,
          });
          
          // Try to play explicitly (handles autoplay restrictions)
          audioElement.play().catch((err) => {
            console.warn('Audio autoplay blocked, user interaction required:', err);
            setAudioBlocked(true);
          });
          
          // If this is from the agent, track speaking state
          if (participant.identity.includes('agent') || participant.identity.includes('bot')) {
            setIsAgentSpeaking(true);
          }
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track, _publication, participant) => {
        console.log('Track unsubscribed:', track.kind, 'from', participant.identity);
        
        if (track.kind === Track.Kind.Audio) {
          const audioElement = audioElementsRef.current.get(participant.identity);
          if (audioElement) {
            track.detach(audioElement);
            audioElement.remove();
            audioElementsRef.current.delete(participant.identity);
          }
          
          if (participant.identity.includes('agent') || participant.identity.includes('bot')) {
            setIsAgentSpeaking(false);
          }
        }
      });

      room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        // Check if agent is speaking
        const agentSpeaking = speakers.some(
          (s) => s.identity.includes('agent') || s.identity.includes('bot')
        );
        setIsAgentSpeaking(agentSpeaking);
      });

      room.on(RoomEvent.DataReceived, (payload, _participant) => {
        try {
          const data = JSON.parse(new TextDecoder().decode(payload));
          console.log('Data received:', data);
          
          // Handle transcript data from agent
          if (data.type === 'transcript') {
            addTranscriptEntry({
              id: `tr_${Date.now()}`,
              role: data.role || 'agent',
              content: data.text || data.content,
              timestamp: Date.now(),
              isFinal: data.isFinal ?? true,
            });
          }
        } catch (e) {
          // Not JSON data, ignore
        }
      });

      room.on(RoomEvent.Disconnected, (reason) => {
        console.log('Disconnected from room:', reason);
        setAgentConnected(false);
        setIsAgentSpeaking(false);
      });

      room.on(RoomEvent.TrackMuted, (publication, participant) => {
        console.log('Track muted:', publication.trackSid, 'from', participant.identity);
      });

      room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
        console.log('Track unmuted:', publication.trackSid, 'from', participant.identity);
      });

      room.on(RoomEvent.LocalTrackPublished, (publication, _participant) => {
        console.log('Local track published:', {
          trackSid: publication.trackSid,
          kind: publication.kind,
          source: publication.source,
        });
      });

      room.on(RoomEvent.TrackPublished, (publication, participant) => {
        console.log('Remote track published:', {
          trackSid: publication.trackSid,
          kind: publication.kind,
          source: publication.source,
          participant: participant.identity,
        });
      });

      room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
        const canPlay = room.canPlaybackAudio;
        console.log('Audio playback status changed, can play:', canPlay);
        if (!canPlay) {
          setAudioBlocked(true);
        } else {
          setAudioBlocked(false);
        }
      });

      // Connect to the room
      await room.connect(LIVEKIT_SERVER_URL, token);
      console.log('Connected to LiveKit room:', room.name);
      console.log('Room metadata:', room.metadata);
      console.log('Room name:', room.name);
      console.log('Local participant:', room.localParticipant.identity);
      console.log('Remote participants count:', room.remoteParticipants.size);
      console.log('Can playback audio:', room.canPlaybackAudio);
      
      // Check if we need user interaction for audio
      if (!room.canPlaybackAudio) {
        console.log('Audio playback requires user interaction');
        setAudioBlocked(true);
      }

      // Enable microphone
      console.log('Enabling microphone...');
      const micTrack = await room.localParticipant.setMicrophoneEnabled(true);
      setIsMicEnabled(true);
      console.log('Microphone enabled:', {
        hasTrack: !!micTrack,
        trackSid: micTrack?.trackSid,
        trackKind: micTrack?.kind,
        isMuted: micTrack?.isMuted,
      });
      
      // Log local participant's published tracks
      console.log('Local participant tracks:', {
        audioTracks: room.localParticipant.audioTrackPublications.size,
        videoTracks: room.localParticipant.videoTrackPublications.size,
      });
      
      room.localParticipant.audioTrackPublications.forEach((pub) => {
        console.log('Local audio track:', {
          sid: pub.trackSid,
          source: pub.source,
          isMuted: pub.isMuted,
        });
      });

      // Check if agent is already in the room and subscribe to existing tracks
      room.remoteParticipants.forEach((participant) => {
        console.log('Existing participant:', participant.identity, participant.metadata);
        const isAgent = participant.identity.toLowerCase().includes('agent') || 
                        participant.identity.toLowerCase().includes('bot') ||
                        participant.identity.toLowerCase().includes('assistant') ||
                        participant.identity.startsWith('agent-');
        if (isAgent) {
          setAgentConnected(true);
          console.log('Agent already in room:', participant.identity);
          
          // Subscribe to existing audio tracks from the agent
          participant.audioTrackPublications.forEach((publication) => {
            console.log('Existing audio publication:', publication.trackSid, publication.isSubscribed);
            if (publication.track) {
              console.log('Attaching existing audio track');
              const audioElement = document.createElement('audio');
              audioElement.autoplay = true;
              (audioElement as HTMLAudioElement & { playsInline: boolean }).playsInline = true;
              audioElement.id = `audio-${participant.identity}`;
              audioElement.volume = 1.0;
              document.body.appendChild(audioElement);
              publication.track.attach(audioElement);
              audioElementsRef.current.set(participant.identity, audioElement);
              audioElement.play().catch((err) => {
                console.warn('Audio autoplay blocked:', err);
              });
            }
          });
        }
      });

      // If no agent yet, log a message
      if (room.remoteParticipants.size === 0) {
        console.log('No other participants in room yet. Waiting for agent to join...');
        addTranscriptEntry({
          id: `sys_${Date.now()}`,
          role: 'system',
          content: 'Connected to room. Waiting for AI agent to join...',
          timestamp: Date.now(),
          isFinal: true,
        });
      }

      // Log room metadata for debugging
      console.log('Room metadata parsed:', room.metadata ? JSON.parse(room.metadata) : 'none');

    } catch (error) {
      console.error('Failed to connect to LiveKit:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to connect to voice room';
      setConnectionError(errorMessage);
      updateStatus('failed');
    }
  };

  const disconnectFromRoom = async () => {
    // Clean up audio elements
    audioElementsRef.current.forEach((element) => {
      element.remove();
    });
    audioElementsRef.current.clear();

    // Disconnect from room
    if (roomRef.current) {
      await roomRef.current.disconnect();
      roomRef.current = null;
    }

    setIsMicEnabled(false);
    setIsAgentSpeaking(false);
    setAgentConnected(false);
    setRoomState(ConnectionState.Disconnected);
  };

  const handleStartCall = async () => {
    setConnectionError(null);
    
    console.log('Starting call with config:', {
      businessId: selectedBusinessId,
      agentId: selectedAgentId,
      agentName: selectedAgent?.name,
      contactPhone,
      contactEmail,
    });
    
    try {
      // Create session in backend - this creates the room and returns a token
      await startSession({
        channel: 'web',
        contactPhone: contactPhone || undefined,
        contactEmail: contactEmail || undefined,
        businessId: selectedBusinessId || undefined,
        agentId: selectedAgentId || undefined,
      });
      
      console.log('Session started, waiting for LiveKit connection...');
      // The useEffect will handle connecting to LiveKit once we have the token
    } catch (error) {
      console.error('Failed to start session:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to start call');
    }
  };

  const handleEndCall = async () => {
    // Disconnect from LiveKit first
    await disconnectFromRoom();

    try {
      // Generate a summary from the transcript
      const userMessages = transcript.filter(t => t.role === 'user');
      const agentMessages = transcript.filter(t => t.role === 'agent');
      
      const summary = transcript.length > 0 
        ? `Voice conversation with ${userMessages.length} user messages and ${agentMessages.length} agent responses.`
        : 'Call ended without conversation';

      await endSession({
        outcome: 'completed',
        summary,
        keyPoints: userMessages.slice(0, 5).map(t => t.content.slice(0, 100)),
      });
    } catch (error) {
      console.error('Failed to end session:', error);
    }
  };

  const handleReset = () => {
    disconnectFromRoom();
    reset();
    setContactPhone('');
    setContactEmail('');
    setConnectionError(null);
    setSelectedAgentId(null);
  };

  const toggleMic = async () => {
    if (!roomRef.current) return;

    const newState = !isMicEnabled;
    await roomRef.current.localParticipant.setMicrophoneEnabled(newState);
    setIsMicEnabled(newState);
  };

  const enableAudio = async () => {
    // Use LiveKit's startAudio method which handles browser autoplay policies
    if (roomRef.current) {
      try {
        await roomRef.current.startAudio();
        console.log('Room audio started via LiveKit');
        setAudioBlocked(false);
      } catch (err) {
        console.error('Failed to start room audio:', err);
      }
    }
    
    // Also try to play all audio elements manually as fallback
    audioElementsRef.current.forEach((element, identity) => {
      console.log('Attempting to play audio for:', identity);
      element.play()
        .then(() => {
          console.log('Audio playing for:', identity);
          setAudioBlocked(false);
        })
        .catch((err) => {
          console.error('Failed to play audio:', err);
        });
    });
  };

  const isActive = status === 'active' || status === 'connecting';

  return (
    <div className="p-8 h-screen flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-white">Test Call</h1>
        <p className="mt-1 text-surface-400">
          Test the voice assistant with a real AI conversation via LiveKit
        </p>
      </div>

      {/* Main content */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-0">
        {/* Left panel - Call controls */}
        <div className="card flex flex-col">
          <div className="card-header">
            <h2 className="font-display text-lg font-semibold text-white">Call Controls</h2>
          </div>
          <div className="card-body flex-1 flex flex-col">
            {!isActive && status !== 'completed' ? (
              <div className="space-y-4">
                {/* Business Selection */}
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Business
                  </label>
                  <select
                    value={selectedBusinessId || ''}
                    onChange={(e) => {
                      setSelectedBusinessId(e.target.value);
                      setSelectedAgentId(null);
                    }}
                    className="input"
                  >
                    <option value="">Select a business...</option>
                    {businesses.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Agent Selection */}
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Agent
                  </label>
                  <select
                    value={selectedAgentId || ''}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    className="input"
                    disabled={!selectedBusinessId}
                  >
                    <option value="">Select an agent...</option>
                    {agents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} {a.isDefault ? '(Default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedAgent && (
                  <div className="p-3 rounded-lg bg-surface-800 border border-surface-700 text-sm">
                    <p className="text-surface-300 font-medium">{selectedAgent.name}</p>
                    <p className="text-surface-500 text-xs mt-1">
                      Voice: {selectedAgent.voiceProvider} / {selectedAgent.voiceId || 'default'}
                    </p>
                    <p className="text-surface-500 text-xs">
                      Model: {selectedAgent.model || 'gpt-4o-realtime-preview'}
                    </p>
                  </div>
                )}

                <div className="border-t border-surface-800 pt-4">
                  <p className="text-xs text-surface-500 mb-3">Optional contact info (for CRM)</p>
                  <div className="space-y-3">
                    <input
                      type="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      className="input"
                      placeholder="Phone (optional)"
                    />
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      className="input"
                      placeholder="Email (optional)"
                    />
                  </div>
                </div>

                <button
                  onClick={handleStartCall}
                  disabled={isLoading || !selectedAgentId}
                  className="btn-primary w-full mt-4"
                >
                  {isLoading ? 'Starting...' : 'Start Call'}
                </button>
              </div>
            ) : (
              <div className="flex-1 flex flex-col">
                {/* Status */}
                <div className="text-center mb-6">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                    status === 'active'
                      ? 'bg-accent-500/20 text-accent-400'
                      : status === 'connecting'
                      ? 'bg-yellow-500/20 text-yellow-400'
                      : 'bg-surface-700 text-surface-400'
                  }`}>
                    <span className={`h-2 w-2 rounded-full ${
                      status === 'active' ? 'bg-accent-400 animate-pulse' : 'bg-current'
                    }`} />
                    {status === 'connecting' ? 'Connecting...' : status === 'active' ? 'Connected' : status}
                  </div>
                  
                  {/* Agent status */}
                  <div className="mt-2">
                    <span className={`text-xs ${agentConnected ? 'text-green-400' : 'text-yellow-400'}`}>
                      {agentConnected ? '✓ Agent connected' : '⏳ Waiting for agent...'}
                    </span>
                  </div>
                  
                  {/* Audio blocked warning */}
                  {audioBlocked && (
                    <div className="mt-3">
                      <button
                        onClick={enableAudio}
                        className="px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm hover:bg-yellow-500/30 transition-colors"
                      >
                        🔊 Click to enable audio
                      </button>
                      <p className="text-xs text-yellow-500/70 mt-1">
                        Browser blocked autoplay
                      </p>
                    </div>
                  )}
                </div>

                {/* Voice visualization */}
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="h-24 flex items-center justify-center mb-4">
                      <VoiceVisualizer isActive={isActive} isSpeaking={isAgentSpeaking} />
                    </div>
                    <p className="text-sm text-surface-400">
                      {isAgentSpeaking ? 'Agent speaking...' : isMicEnabled ? 'Listening...' : 'Microphone muted'}
                    </p>
                  </div>
                </div>

                {/* Controls */}
                <div className="flex items-center justify-center gap-4 mt-6">
                  <button
                    onClick={toggleMic}
                    className={`p-4 rounded-full transition-colors ${
                      isMicEnabled
                        ? 'bg-primary-600 text-white'
                        : 'bg-surface-700 text-surface-400 hover:bg-surface-600'
                    }`}
                    title={isMicEnabled ? 'Mute microphone' : 'Unmute microphone'}
                  >
                    {isMicEnabled ? <MicOnIcon /> : <MicOffIcon />}
                  </button>
                  <button
                    onClick={handleEndCall}
                    className="p-4 rounded-full bg-red-600 text-white hover:bg-red-500 transition-colors"
                    title="End call"
                  >
                    <PhoneOffIcon />
                  </button>
                </div>
              </div>
            )}

            {status === 'completed' && (
              <div className="mt-4">
                <button onClick={handleReset} className="btn-secondary w-full">
                  Start New Call
                </button>
              </div>
            )}

            {(error || connectionError) && (
              <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                {error || connectionError}
              </div>
            )}
          </div>
        </div>

        {/* Middle panel - Transcript */}
        <div className="card flex flex-col">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-white">Transcript</h2>
            {transcript.length > 0 && (
              <span className="text-xs text-surface-500">{transcript.length} messages</span>
            )}
          </div>
          <div className="flex-1 min-h-0">
            <TranscriptView entries={transcript} />
          </div>
        </div>

        {/* Right panel - Session info */}
        <div className="card flex flex-col">
          <div className="card-header">
            <h2 className="font-display text-lg font-semibold text-white">Session Info</h2>
          </div>
          <div className="card-body space-y-4">
            {conversationId ? (
              <>
                <InfoRow label="Conversation ID" value={conversationId} mono />
                <InfoRow label="Room Name" value={roomName || '-'} mono />
                <InfoRow label="Status" value={status} />
                <InfoRow label="Room State" value={roomState} />
                <InfoRow label="Agent" value={agentConnected ? 'Connected' : 'Not connected'} />
                
                {selectedAgent && (
                  <div className="pt-4 border-t border-surface-800">
                    <h3 className="text-sm font-medium text-surface-300 mb-3">Agent Config</h3>
                    <div className="space-y-2">
                      <InfoRow label="Name" value={selectedAgent.name} small />
                      <InfoRow label="Voice" value={`${selectedAgent.voiceProvider}/${selectedAgent.voiceId || 'default'}`} small />
                      <InfoRow label="Model" value={selectedAgent.model || 'gpt-4o-realtime'} small />
                    </div>
                  </div>
                )}
                
                {contact && (
                  <div className="pt-4 border-t border-surface-800">
                    <h3 className="text-sm font-medium text-surface-300 mb-3">Contact</h3>
                    <div className="space-y-2">
                      <InfoRow label="ID" value={contact.id} mono small />
                      <InfoRow label="Name" value={contact.name || '-'} small />
                      <InfoRow label="Phone" value={contact.phone || '-'} small />
                      <InfoRow label="Email" value={contact.email || '-'} small />
                      {isNewContact && (
                        <span className="inline-block px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400">
                          New Contact
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {token && (
                  <div className="pt-4 border-t border-surface-800">
                    <h3 className="text-sm font-medium text-surface-300 mb-2">LiveKit Token</h3>
                    <code className="block text-xs text-surface-500 break-all bg-surface-800 rounded p-2 max-h-20 overflow-auto">
                      {token.slice(0, 50)}...
                    </code>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-surface-800 mb-3">
                  <svg className="w-6 h-6 text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-surface-500 text-sm">
                  Start a call to see session information
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  mono,
  small,
}: {
  label: string;
  value: string;
  mono?: boolean;
  small?: boolean;
}) {
  return (
    <div className={small ? 'text-sm' : ''}>
      <span className="text-surface-500">{label}: </span>
      <span className={`text-surface-200 ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  );
}

function MicOnIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
    </svg>
  );
}

function PhoneOffIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
    </svg>
  );
}
